import { Formik } from 'formik';
import { useTransition } from 'react';
import { submitFlexiLeaseForm } from '@/actions/flexi-lease-form';
import {
    DropdownSelect,
    FormDivider,
    TextInput,
    DateOfBirth,
    HoneypotField,
    ErrorBlock,
} from '@/components/formComponents';
import { toast } from 'react-hot-toast';
import { Button, FlexiLeaseFormInfoLegend, PageMainCard } from '@/components';
import validationSchema from '@/validation/flexiLeaseFormValidation';
import ValuesDisplay from '../formComponents/ValuesDisplay';
import { FormRow } from '@/components/formComponents/helpers';
import { CurrentVehicleProps } from '@/utils/returnDateCalculator/types';
import {
    ALL_VEHICLES_QUERYResult,
    FLEXI_QUOTE_FORM_CONTENT_QUERYResult,
} from 'sanity.types';

interface FlexiLeaseFormProps {
    vehicles: ALL_VEHICLES_QUERYResult['vehicles'];
    flexiQuoteForm: FLEXI_QUOTE_FORM_CONTENT_QUERYResult;
}

interface FlexiLeaseFormValues {
    car: CurrentVehicleProps | null;
    contractTerm: string;
    annualMileage: string;
    fullName: string;
    emailAddress: string;
    phoneNumber: string;
    housePostcode: string;
    occupation: string;
    dateOfBirth: string;
    dobDay: string;
    dobMonth: string;
    dobYear: string;
}

const FlexiLeaseForm = ({ vehicles, flexiQuoteForm }: FlexiLeaseFormProps) => {
    const currentVehicle = null;
    const [isPending, startTransition] = useTransition();

    const initialValues: FlexiLeaseFormValues = {
        car: currentVehicle,
        contractTerm: '',
        annualMileage: '',
        fullName: '',
        emailAddress: '',
        phoneNumber: '',
        housePostcode: '',
        occupation: '',
        dateOfBirth: '',
        dobDay: '',
        dobMonth: '',
        dobYear: '',
    };

    // Vehicle drop down options
    const vehiclesOptions = vehicles.map((vehicle) => ({
        label: vehicle.title || '',
        value: vehicle._id,
    }));

    // Lease length drop down options
    const range = (start: number, end: number, modifier?: number) => {
        const _modifier = modifier ? modifier : 1;
        return Array(end / (modifier || 1) - start / (modifier || 1) + 1)
            .fill(null)
            .map((_, idx) => {
                const addition = idx * _modifier;
                return {
                    label: start + addition,
                    value: start + addition,
                };
            });
    };

    return (
        <PageMainCard>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur={false}
                onSubmit={async (
                    values,
                    { resetForm, setFieldError, setSubmitting }
                ) => {
                    // Scroll to top immediately to show the user feedback area
                    window.scrollTo({ top: 0, behavior: 'smooth' });

                    startTransition(async () => {
                        try {
                            // Create FormData from Formik values
                            const formData = new FormData();

                            // Handle dropdown objects - stringify for transmission
                            formData.append('car', JSON.stringify(values.car));
                            formData.append(
                                'contractTerm',
                                JSON.stringify(values.contractTerm)
                            );
                            formData.append(
                                'annualMileage',
                                JSON.stringify(values.annualMileage)
                            );

                            // Handle string fields
                            formData.append('fullName', values.fullName);
                            formData.append(
                                'emailAddress',
                                values.emailAddress
                            );
                            formData.append('phoneNumber', values.phoneNumber);
                            formData.append(
                                'housePostcode',
                                values.housePostcode
                            );
                            formData.append(
                                'occupation',
                                values.occupation || ''
                            );
                            formData.append('dateOfBirth', values.dateOfBirth);
                            formData.append('dobDay', values.dobDay);
                            formData.append('dobMonth', values.dobMonth);
                            formData.append('dobYear', values.dobYear);

                            // Call server action
                            const result = await submitFlexiLeaseForm(formData);

                            if (result.isBot) {
                                // Silently swallow — bot thinks it succeeded, we do nothing
                                return;
                            }

                            if (result.success) {
                                toast.success(
                                    "Flexi lease quote request submitted successfully, thanks! We'll get back to you as soon as possible.",
                                    {
                                        duration: 10000,
                                    }
                                );
                                resetForm();
                            } else {
                                // Handle validation errors from server
                                if (result.fieldErrors) {
                                    Object.entries(result.fieldErrors).forEach(
                                        ([field, message]) => {
                                            setFieldError(
                                                field,
                                                message as string
                                            );
                                        }
                                    );
                                }

                                // Show error toast
                                toast.error(
                                    result.error ||
                                        'Something went wrong. Please try again.',
                                    {
                                        duration: 6000,
                                    }
                                );

                                // Log errors for debugging
                                if (result.validationErrors) {
                                    console.log(
                                        'Validation errors:',
                                        result.validationErrors
                                    );
                                }
                            }
                        } catch (error) {
                            console.error('Form submission error:', error);
                            toast.error(
                                'An unexpected error occurred. Please try again.',
                                {
                                    duration: 6000,
                                }
                            );
                        } finally {
                            setSubmitting(false);
                        }
                    });
                }}
            >
                {({ handleSubmit, errors, values, isSubmitting }) => {
                    const errorsExist = Object.keys(errors).length > 0;
                    return (
                        <form
                            onSubmit={(e: React.FormEvent) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            className="w-full"
                        >
                            {isSubmitting && errorsExist && <ErrorBlock />}

                            <FlexiLeaseFormInfoLegend
                                flexiQuoteFormData={flexiQuoteForm}
                            />

                            <FormRow
                                label={`Select a vehicle:`}
                                content={
                                    <DropdownSelect
                                        name="car"
                                        options={vehiclesOptions}
                                        placeholder="Select a vehicle"
                                    />
                                }
                            />

                            <FormRow
                                label="Approximate contract term:"
                                content={
                                    <DropdownSelect
                                        name="contractTerm"
                                        options={range(6, 24)}
                                        placeholder="Contract term (months)"
                                    />
                                }
                            />

                            <FormRow
                                label="Approximate ANNUAL mileage:"
                                content={
                                    <DropdownSelect
                                        name="annualMileage"
                                        options={range(10000, 25000, 1000)}
                                        placeholder="Annual mileage (miles)"
                                    />
                                }
                            />

                            <HoneypotField />

                            <FormDivider />

                            <FormRow
                                label="Full name:"
                                content={
                                    <TextInput
                                        name="fullName"
                                        placeholder="Your full name"
                                    />
                                }
                            />

                            <FormRow
                                label="Email address:"
                                content={
                                    <TextInput
                                        name="emailAddress"
                                        placeholder="Your email address"
                                    />
                                }
                            />

                            <FormRow
                                label="Best contact number:"
                                content={
                                    <TextInput
                                        name="phoneNumber"
                                        placeholder="Your phone number"
                                    />
                                }
                            />

                            <FormRow
                                label="Postcode:"
                                content={
                                    <TextInput
                                        name="housePostcode"
                                        placeholder="Your postcode"
                                    />
                                }
                            />

                            <FormRow
                                label="Occupation:"
                                required={false}
                                content={
                                    <TextInput
                                        name="occupation"
                                        placeholder="Your occupation"
                                    />
                                }
                            />

                            <FormRow
                                label="Date of birth:"
                                content={<DateOfBirth name="dateOfBirth" />}
                            />

                            <ValuesDisplay values={values} />

                            <div className="flex justify-between">
                                <div className="flex text-sm italic items-center required text-gray-500">
                                    Required
                                </div>
                                <div className="w-full justify-end flex">
                                    <Button
                                        label={
                                            isPending || isSubmitting
                                                ? 'Submitting...'
                                                : 'Submit'
                                        }
                                        disabled={isPending || isSubmitting}
                                        className="!text-3xl mx-auto mb-4 mt-2"
                                    />
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </PageMainCard>
    );
};

export default FlexiLeaseForm;
